/**
 * RobotPhysics — lightweight local physics for robot movement.
 *
 * Replaces instant teleportation with velocity-based movement:
 * - Acceleration toward desired direction (traction limited by wheels/stability)
 * - Ground friction & air drag
 * - Smooth braking when no movement intent
 * - Smooth heading rotation (turning radius)
 * - Acceleration-based visual tilt (lean into turns, lean back on accel)
 * - Robot-robot collision (push apart)
 * - Map boundary clamping
 *
 * The BehaviorEngine sets `robot.moveIntentX/Y/Speed` each tick.
 * This system reads that intent and updates position/velocity/visuals.
 */

import { Robot, RobotState } from '@/entities/Robot';
import { TILE_SIZE, GAME_WIDTH, GAME_HEIGHT, TileType, MUD_SPEED_MULTIPLIER, COMPONENT_SPACING, DECOR_INTERACTIONS, DecorInteraction } from '@/config/GameConfig';
import { GOD } from '@/config/GodMode';
import { MAP_GRID, getDecorAt } from '@/map/MapData';
import { SoundManager } from '@/systems/SoundManager';
import { ScoreManager } from '@/systems/ScoreManager';

// ── Tuning constants ──

/** Base acceleration (px/s²). Actual = BASE_ACCEL × (traction / mass). */
const BASE_ACCEL = 300;

/** Friction coefficient on ground (deceleration factor per second). */
const GROUND_FRICTION = 6.0;

/** Extra friction multiplier for unstable robots (0 = no extra, 1 = double). */
const INSTABILITY_FRICTION_BONUS = 1.5;

/** Braking deceleration multiplier (how fast the robot stops when no intent). */
const BRAKE_FACTOR = 10.0;

/** Maximum angular velocity (rad/s) for heading changes. */
const MAX_ANGULAR_VEL = 6.0;

/** Angular acceleration (rad/s²). */
const ANGULAR_ACCEL = 15.0;

/** Angular friction (deceleration rad/s per second). */
const ANGULAR_FRICTION = 8.0;

/** Maximum visual tilt from dynamics (radians). */
const MAX_DYNAMIC_TILT = 0.35; // ~20°

/** Tilt response speed (how fast visual tilt follows physics). */
const TILT_SMOOTHING = 8.0;

/** Minimum velocity to consider robot "moving" (px/s). */
const MIN_VELOCITY = 0.5;

/** Collision push strength. */
const COLLISION_PUSH = 150;

/** Minimum flight speed — flying robots cannot hover (px/s). */
const MIN_FLIGHT_SPEED = 40;

/** Air friction (much lower than ground — planes glide). */
const AIR_FRICTION = 0.3;

/** Critical tilt angle (radians) beyond which the robot tips over and is destroyed. */
const TIPOVER_ANGLE = 0.7; // ~40°

/** How fast accumulated tilt builds toward tipover (higher = tips faster). */
const TIPOVER_BUILDUP = 2.0;

// ── State ──
const visualTilt = new Map<string, number>();
const prevVx = new Map<string, number>();
const prevVy = new Map<string, number>();
const tipoverAccum = new Map<string, number>();
/** Accumulated time (seconds) a flyer has been in critical spin before crash triggers. */
const crashSpinAccum = new Map<string, number>();

// ── Jumper hop constants ──
const HOP_COMPRESS_TIME = 0.35;   // seconds of compression (slow squash)
const HOP_LAUNCH_TIME = 0.08;     // seconds of launch snap (fast release)
const HOP_AIR_TIME = 0.4;         // seconds airborne (parabolic arc)
const HOP_LAND_TIME = 0.06;       // seconds of landing squash
const HOP_CYCLE = HOP_COMPRESS_TIME + HOP_LAUNCH_TIME + HOP_AIR_TIME + HOP_LAND_TIME; // ~0.89s
const HOP_HEIGHT = 18;            // px peak height during hop
const HOP_SQUASH_AMOUNT = 0.25;   // max squash ratio (25% shorter during compression)

// ── Public API ──

/**
 * Clear cached physics state (call on scene restart).
 */
export function resetPhysicsState(): void {
  visualTilt.clear();
  prevVx.clear();
  prevVy.clear();
  tipoverAccum.clear();
  crashSpinAccum.clear();
}

/**
 * Set a robot's movement intent (called by BehaviorEngine movement functions).
 * dirX/dirY should be a unit vector (or zero for stop).
 */
export function setMoveIntent(robot: Robot, dirX: number, dirY: number, speed: number): void {
  robot.moveIntentX = dirX;
  robot.moveIntentY = dirY;
  robot.moveIntentSpeed = speed;
}

/**
 * Run one physics tick for a single robot.
 * Call AFTER behavior tick, BEFORE rendering.
 */
export function updateRobotPhysics(robot: Robot, dt: number): void {
  if (!robot.alive || dt <= 0) return;

  const dtS = dt / 1000;

  // Tick slow debuff
  robot.tickSlow(dtS);

  // ── Crashing flyer: spiral physics only, skip everything else ──
  if (robot.isCrashing) {
    robot.updateCrashPhysics(dtS);
    return;
  }

  // Fallen robots: still run movement physics (dragging) but visuals are
  // handled by topple system — skip container rotation/position at the end.
  const fallen = robot.isFallen;

  // ── Radar sweep: advance sweep angle or lock-on to detected enemies ──
  if (robot.hasDirectionComponent && robot.sweepSpeed > 0) {
    const dtS2 = dt / 1000;
    if (robot.sweepLockTarget) {
      // Lock-on: smoothly rotate sweep toward the average enemy position
      const dx = robot.sweepLockTarget.x - robot.x;
      const dy = robot.sweepLockTarget.y - robot.y;
      const targetAngle = Math.atan2(dy, dx) - robot.heading;
      // Normalize target to [0, 2π)
      let normTarget = ((targetAngle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
      let diff = normTarget - robot.sweepAngle;
      // Shortest rotation path
      if (diff > Math.PI) diff -= Math.PI * 2;
      if (diff < -Math.PI) diff += Math.PI * 2;
      // Snap quickly — 3x sweep speed for responsive lock-on
      const maxTurn = robot.sweepSpeed * 3 * dtS2;
      if (Math.abs(diff) < maxTurn) {
        robot.sweepAngle = normTarget;
      } else {
        robot.sweepAngle += Math.sign(diff) * maxTurn;
      }
    } else {
      // Free sweep: rotate continuously
      robot.sweepAngle += robot.sweepSpeed * dtS2;
    }
    // Wrap to [0, 2π)
    robot.sweepAngle = ((robot.sweepAngle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
  }

  // Fixed robots (no movement components): no physics, no tilt, stay in place
  // Fixed robots (no movement components): no physics movement, but face toward target
  if (robot.moveForce <= 0) {
    robot.vx = 0;
    robot.vy = 0;
    if (!fallen) {
      // Flip toward current target (deadzone + cooldown prevent oscillation)
      if (!isNaN(robot.faceTargetX)) {
        const dx = robot.faceTargetX - robot.x;
        const dz = Math.max(8, GOD.flipDeadzone);
        const wantFlip = dx < -dz ? true : dx > dz ? false : robot.flipped;
        if (wantFlip !== robot.flipped) {
          const now = robot.scene.time.now;
          if (now - robot.lastFlipTime >= GOD.flipCooldownMs) {
            robot.flipped = wantFlip;
            robot.lastFlipTime = now;
          }
        }
      }
      const sx = robot.flipped ? -robot.renderScale : robot.renderScale;
      robot.container.setScale(sx, robot.renderScale);
      robot.container.setRotation(0);
      robot.container.setPosition(robot.x, robot.y);
    }
    return;
  }

  // ── Stun tick: count down stun timer, skip movement intent while stunned ──
  if (robot.stunTimer > 0) {
    robot.stunTimer = Math.max(0, robot.stunTimer - dtS);
    // Visual feedback: gentle alpha pulse (skip on last frame to avoid lingering)
    if (robot.stunTimer > 0) {
      robot.container.setAlpha(0.7 + 0.3 * Math.sin(robot.stunTimer * 8));
    }

    // Spinning stars just below the energy bar
    if (!robot.stunStars) {
      robot.stunStars = robot.scene.add.container(robot.x, robot.y).setDepth(robot.container.depth + 2);
      for (const ch of ['★', '✦', '★']) {
        const star = robot.scene.add.text(0, 0, ch, {
          fontSize: '16px', color: '#ffdd44', fontFamily: 'monospace',
          stroke: '#000000', strokeThickness: 3,
        }).setOrigin(0.5);
        robot.stunStars.add(star);
      }
    }
    // Compute energy bar Y (same formula as updateDebugOverlays)
    let topLocalY = 0;
    for (const c of robot.components) {
      const cy = c.localY * GOD.componentSpacingMult;
      if (cy < topLocalY) topLocalY = cy;
    }
    const actualScale = Math.abs(robot.container.scaleY);
    const halfComp = (COMPONENT_SPACING / 2) * actualScale;
    const topWorldOff = Math.abs(topLocalY) * actualScale + halfComp;
    const baseOff = (topWorldOff + 2) * GOD.robotBarOffsetMult;
    const enBarY = robot.y - baseOff + 3; // energy bar Y = hpY + barGap(3)
    const starsY = enBarY + 6; // just below energy bar
    robot.stunStars.setPosition(robot.x, starsY);
    robot.stunStarsAngle += dtS * 3;
    const starRadius = 16;
    const children = robot.stunStars.getAll() as Phaser.GameObjects.Text[];
    for (let i = 0; i < children.length; i++) {
      const a = robot.stunStarsAngle + (Math.PI * 2 * i) / children.length;
      children[i].setPosition(Math.cos(a) * starRadius, Math.sin(a) * starRadius * 0.4);
      children[i].setAlpha(0.7 + 0.3 * Math.sin(a * 2));
    }

    if (robot.stunTimer <= 0) {
      robot.stunStars.destroy();
      robot.stunStars = null;
      robot.container.setAlpha(1);
    }
  } else {
    if (robot.stunStars) {
      robot.stunStars.destroy();
      robot.stunStars = null;
    }
    if (robot.container.alpha !== 1) {
      robot.container.setAlpha(1);
    }
  }

  const stability = robot.stability.score;
  const mass = Math.max(1, robot.totalMass);

  // ── 1. Compute traction (depends on wheels/stability) ──
  // More wheels + better stability = better grip
  const wheelCount = robot.stability.supportCount;
  const traction = Math.max(0.2, stability) * Math.min(wheelCount, 4) / 2;
  const accel = BASE_ACCEL * traction / (mass / 25); // normalize to reference mass

  // ── 2. Desired velocity from intent (suppressed while stunned) ──
  const stunned = robot.isStunned;
  // Mud / decoration slowdown: ground robots on MUD tiles or FLAQUE decorations move slower
  let mudMult = 1;
  {
    const tCol = Math.floor(robot.x / TILE_SIZE);
    const tRow = Math.floor(robot.y / TILE_SIZE);
    const currentTile = (tRow >= 0 && tRow < MAP_GRID.length && tCol >= 0 && tCol < (MAP_GRID[0]?.length ?? 0))
      ? MAP_GRID[tRow][tCol] : null;
    // Update terrain modifier on robot (popup + stat buffs)
    robot.updateTerrain(currentTile);
    if (!robot.hasWings() && currentTile === TileType.MUD) {
      mudMult = MUD_SPEED_MULTIPLIER;
    }
    // Decoration slow effect (e.g. puddle)
    if (!robot.hasWings()) {
      const decor = getDecorAt(tCol, tRow);
      if (decor) {
        const cfg = DECOR_INTERACTIONS[decor.type];
        if (cfg.interaction === DecorInteraction.SLOW && cfg.slowMultiplier) {
          mudMult = Math.min(mudMult, cfg.slowMultiplier);
        }
      }
    }
  }
  // Low energy penalty: mobile (wheeled) and flying robots slow to a crawl
  let energyMult = 1;
  if (robot.maxEnergy > 0 && robot.moveForce > 0) {
    const energyRatio = robot.currentEnergy / robot.maxEnergy;
    if (energyRatio < GOD.lowEnergyThreshold) {
      // Lerp from full speed at threshold down to lowEnergySpeedMult at 0%
      const t = energyRatio / GOD.lowEnergyThreshold; // 0..1
      energyMult = GOD.lowEnergySpeedMult + (1 - GOD.lowEnergySpeedMult) * t;
    }
  }
  const desiredVx = stunned ? 0 : robot.moveIntentX * robot.moveIntentSpeed * mudMult * energyMult;
  const desiredVy = stunned ? 0 : robot.moveIntentY * robot.moveIntentSpeed * mudMult * energyMult;
  const hasIntent = !stunned && robot.moveIntentSpeed > 0;

  // ── 3. Accelerate toward desired velocity ──
  if (hasIntent) {
    const dvx = desiredVx - robot.vx;
    const dvy = desiredVy - robot.vy;
    const dvMag = Math.sqrt(dvx * dvx + dvy * dvy);
    if (dvMag > 0.1) {
      const acc = Math.min(accel * dtS, dvMag);
      robot.vx += (dvx / dvMag) * acc;
      robot.vy += (dvy / dvMag) * acc;
    } else {
      robot.vx = desiredVx;
      robot.vy = desiredVy;
    }
  }

  // ── 4. Apply friction / braking ──
  const isFlyer = robot.hasWings();
  const speed = Math.sqrt(robot.vx * robot.vx + robot.vy * robot.vy);
  if (speed > MIN_VELOCITY) {
    let frictionRate: number;
    if (isFlyer) {
      // Air drag only — no ground braking, planes always glide
      frictionRate = AIR_FRICTION;
    } else if (!hasIntent) {
      // Ground braking: stronger deceleration
      frictionRate = BRAKE_FACTOR;
    } else {
      // Rolling friction + instability penalty
      frictionRate = GROUND_FRICTION * (1 + (1 - stability) * INSTABILITY_FRICTION_BONUS);
    }
    // Exponential decay: frame-rate independent friction.
    // Linear Euler (1 - rate*dt) breaks at high dt (x4 speed kills 80% per frame).
    // exp(-rate*dt) is stable at any time step.
    const minSpeed = isFlyer ? MIN_FLIGHT_SPEED : 0;
    const factor = Math.exp(-frictionRate * dtS);
    const newSpeed = Math.max(minSpeed, speed * factor);
    const speedFactor = newSpeed / speed;
    robot.vx *= speedFactor;
    robot.vy *= speedFactor;
  } else if (!hasIntent) {
    if (isFlyer && speed < MIN_FLIGHT_SPEED) {
      // Flying robot with no intent: maintain minimum forward speed along heading
      robot.vx = Math.cos(robot.heading) * MIN_FLIGHT_SPEED;
      robot.vy = Math.sin(robot.heading) * MIN_FLIGHT_SPEED;
    } else if (!isFlyer) {
      robot.vx = 0;
      robot.vy = 0;
    }
  }

  // ── 4b. Flight degradation: damaged flight components + instability cause erratic behavior ──
  if (isFlyer) {
    const integrity = robot.flightIntegrity;
    // Instability spin: unstable flyers spin faster, causing unpredictable trajectory
    // spinFactor combines structural instability + flight damage
    const instability = 1 - stability; // 0 = perfectly stable, 1 = maximally unstable
    const damageFactor = integrity < 1 ? (1 - integrity) * (1 - integrity) : 0;
    const spinFactor = Math.max(instability * 0.6, damageFactor);

    if (spinFactor > 0.05) {
      // Target spin rate: higher instability = faster spin (up to ~4 rad/s = ~230°/s)
      const targetSpinRate = spinFactor * 4.0;
      // Smooth ramp toward target spin rate
      robot.spinRate += (targetSpinRate - robot.spinRate) * Math.min(1, 2 * dtS);

      // Apply spin to heading — makes the flyer rotate continuously
      robot.heading += robot.spinRate * dtS;
      while (robot.heading > Math.PI) robot.heading -= Math.PI * 2;
      while (robot.heading < -Math.PI) robot.heading += Math.PI * 2;

      // Spin-induced drift: centripetal-like force pushes perpendicular to heading
      // The faster the spin, the more erratic the trajectory
      const perpX = -Math.sin(robot.heading);
      const perpY = Math.cos(robot.heading);
      const driftForce = robot.spinRate * 40 * dtS;
      robot.vx += perpX * driftForce * (0.5 + Math.sin(robot.heading * 3) * 0.5);
      robot.vy += perpY * driftForce * (0.5 + Math.cos(robot.heading * 3) * 0.5);

      // Random jitter on top (existing drift behavior, scaled by damage)
      if (damageFactor > 0) {
        const jitter = damageFactor * 80 * dtS;
        robot.vx += (Math.random() - 0.5) * jitter;
        robot.vy += (Math.random() - 0.5) * jitter;
      }

      // ── Crash trigger: sustained high spin → robot loses control and crashes ──
      // spinFactor > 0.35 = unstable enough to eventually lose control
      const CRASH_SPIN_THRESHOLD = 0.35;
      const CRASH_BUILDUP_TIME = 2.0; // seconds of sustained critical spin before crash
      if (spinFactor > CRASH_SPIN_THRESHOLD) {
        const acc = (crashSpinAccum.get(robot.id) ?? 0) + dtS;
        crashSpinAccum.set(robot.id, acc);
        if (acc >= CRASH_BUILDUP_TIME) {
          crashSpinAccum.delete(robot.id);
          robot.startCrash();
          return; // crash takes over — skip rest of physics
        }
      } else {
        // Below threshold — slowly recover
        const acc = crashSpinAccum.get(robot.id) ?? 0;
        if (acc > 0) crashSpinAccum.set(robot.id, Math.max(0, acc - dtS * 0.5));
      }
    } else {
      // Stable flyer — decay spin back to zero
      robot.spinRate *= Math.max(0, 1 - 3 * dtS);
      if (Math.abs(robot.spinRate) < 0.01) robot.spinRate = 0;
      // Reset crash accumulator
      crashSpinAccum.delete(robot.id);
    }
  }

  // ── 4c. Edge repulsion: soft push away from map borders for flyers ──
  if (isFlyer) {
    const edgeMargin = GOD.flyerEdgeMargin * TILE_SIZE;
    const repForce = GOD.flyerEdgeRepulsion;

    const dLeft   = robot.x;
    const dRight  = GAME_WIDTH - robot.x;
    const dTop    = robot.y;
    const dBottom = GAME_HEIGHT - robot.y;

    let repX = 0;
    let repY = 0;
    if (dLeft < edgeMargin) {
      const t = 1 - dLeft / edgeMargin;
      repX += t * t * repForce;
    }
    if (dRight < edgeMargin) {
      const t = 1 - dRight / edgeMargin;
      repX -= t * t * repForce;
    }
    if (dTop < edgeMargin) {
      const t = 1 - dTop / edgeMargin;
      repY += t * t * repForce;
    }
    if (dBottom < edgeMargin) {
      const t = 1 - dBottom / edgeMargin;
      repY -= t * t * repForce;
    }

    robot.vx += repX * dtS;
    robot.vy += repY * dtS;
  }

  // ── 5. Update position ──
  const newX = robot.x + robot.vx * dtS;
  const newY = robot.y + robot.vy * dtS;

  // Clamp to map bounds
  const margin = TILE_SIZE / 2;
  const clampedX = Math.max(margin, Math.min(GAME_WIDTH - margin, newX));
  const clampedY = Math.max(margin, Math.min(GAME_HEIGHT - margin, newY));

  // Wall collision: check if new tile is walkable for ground units
  // Ground-walkable: PATH, PLACEMENT, BASE, SPAWN, MUD, GRILLE, GROUND (not WALL, WATER)
  const isGroundWalkable = (t: TileType | undefined): boolean =>
    t === TileType.PATH || t === TileType.PLACEMENT || t === TileType.BROKEN_PLACEMENT ||
    t === TileType.BASE || t === TileType.SPAWN ||
    t === TileType.MUD || t === TileType.GRILLE || t === TileType.GROUND;

  /** Check if decoration at tile blocks movement */
  const isDecorBlocked = (col: number, row: number): boolean => {
    const d = getDecorAt(col, row);
    if (!d) return false;
    const cfg = DECOR_INTERACTIONS[d.type];
    return cfg.interaction === DecorInteraction.BLOCK;
  };

  const tileCol = Math.floor(clampedX / TILE_SIZE);
  const tileRow = Math.floor(clampedY / TILE_SIZE);
  const tile = MAP_GRID[tileRow]?.[tileCol];
  const walkable = isGroundWalkable(tile) && !isDecorBlocked(tileCol, tileRow);

  if (isFlyer) {
    // Flying robots: bounce velocity off map edges but keep heading smooth
    // Velocity reflects instantly (physics), heading turns gradually (visual)
    if (clampedX !== newX) { robot.vx = -robot.vx; }
    if (clampedY !== newY) { robot.vy = -robot.vy; }
    robot.x = clampedX;
    robot.y = clampedY;
    // Heading will catch up via step 6 (smooth rotation toward velocity)
  } else if (walkable) {
    robot.x = clampedX;
    robot.y = clampedY;
  } else {
    // Hit a wall/decor — bounce velocity to zero on blocked axis
    // Try X only
    const txCol = Math.floor(clampedX / TILE_SIZE);
    const txRow = Math.floor(robot.y / TILE_SIZE);
    const xOk = isGroundWalkable(MAP_GRID[txRow]?.[txCol]) && !isDecorBlocked(txCol, txRow);
    if (xOk) {
      robot.x = clampedX;
      robot.vy = 0;
    } else {
      robot.vx = 0;
    }
    // Try Y only
    const tyCol = Math.floor(robot.x / TILE_SIZE);
    const tyRow = Math.floor(clampedY / TILE_SIZE);
    const yOk = isGroundWalkable(MAP_GRID[tyRow]?.[tyCol]) && !isDecorBlocked(tyCol, tyRow);
    if (yOk) {
      robot.y = clampedY;
    } else {
      robot.vy = 0;
    }
  }

  // ── 5b. Kill-zone decorations (chasm): destroy unit if fully on it ──
  if (!isFlyer) {
    const kCol = Math.floor(robot.x / TILE_SIZE);
    const kRow = Math.floor(robot.y / TILE_SIZE);
    const killDecor = getDecorAt(kCol, kRow);
    if (killDecor && DECOR_INTERACTIONS[killDecor.type].interaction === DecorInteraction.KILL) {
      robot.takeDamage(robot.currentHp + 999); // instant kill
      return;
    }
  }

  // ── 5c. Accumulate distance traveled for scoring ──
  ScoreManager.addRobotDistance(Math.sqrt(robot.vx * robot.vx + robot.vy * robot.vy) * dtS);

  // ── 6. Heading (smooth rotation toward movement direction) ──
  const curSpeed = Math.sqrt(robot.vx * robot.vx + robot.vy * robot.vy);
  if (curSpeed > MIN_VELOCITY) {
    const targetHeading = Math.atan2(robot.vy, robot.vx);

    if (!isFlyer) {
      // Ground robots: snap heading to left (π) or right (0) instantly based on velocity.
      // They don't physically rotate — only flip horizontally — so smooth rotation is wrong.
      // This ensures the sensor/radar direction matches the movement direction immediately.
      if (robot.vx > 0.5) {
        robot.heading = 0;              // facing right
      } else if (robot.vx < -0.5) {
        robot.heading = Math.PI;        // facing left
      }
      // Otherwise keep current heading (nearly vertical movement or slow horizontal)
    } else {
      // Flying robots: smooth rotation toward velocity direction
      // When spinning, the spin dominates — reduce heading correction
      const spinInfluence = Math.min(1, Math.abs(robot.spinRate) / 2); // 0 = no spin, 1 = full spin
      if (spinInfluence > 0.8) {
        // Spinning hard — skip heading correction entirely, spin controls heading
      } else {
      let diff = targetHeading - robot.heading;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;

      const maxTurn = MAX_ANGULAR_VEL * Math.max(0.3, stability) * dtS * (1 - spinInfluence);
      const prevHeading = robot.heading;
      if (Math.abs(diff) < maxTurn) {
        robot.heading = targetHeading;
      } else {
        robot.heading += Math.sign(diff) * maxTurn;
      }
      while (robot.heading > Math.PI) robot.heading -= Math.PI * 2;
      while (robot.heading < -Math.PI) robot.heading += Math.PI * 2;

      // Servo sound on significant direction changes (>45°/s actual turn)
      let headingDelta = robot.heading - prevHeading;
      while (headingDelta > Math.PI) headingDelta -= Math.PI * 2;
      while (headingDelta < -Math.PI) headingDelta += Math.PI * 2;
      if (Math.abs(headingDelta) > Math.PI / 4 * dtS && Math.random() < 0.08) {
        SoundManager.play('servoTurn');
      }
      } // end spinInfluence else
    }
  }

  // ── 7. Orientation: flying vs ground ──

  if (!isFlyer) {
    // Ground robots: face toward target when attacking, else face velocity direction
    // Deadzone + cooldown prevent rapid flip oscillation
    let wantFlip = robot.flipped;
    const dz = Math.max(8, GOD.flipDeadzone);
    if (robot.state === RobotState.ATTACKING && !isNaN(robot.faceTargetX)) {
      const dx = robot.faceTargetX - robot.x;
      if (dx > dz) wantFlip = false;
      else if (dx < -dz) wantFlip = true;
    } else if (robot.vx > 2) {
      wantFlip = false;
    } else if (robot.vx < -2) {
      wantFlip = true;
    }
    if (wantFlip !== robot.flipped) {
      const now = robot.scene.time.now;
      if (now - robot.lastFlipTime >= GOD.flipCooldownMs) {
        robot.flipped = wantFlip;
        robot.lastFlipTime = now;
      }
    }
  }
  // Flying robots handle rotation in step 10 (no flip, full heading rotation)

  // ── 8. Visual tilt (CoM-aware physics tilt) ──
  //
  // The key idea: tilt depends on the torque that acceleration exerts
  // around the support center, via the center of mass offset.
  //
  // When the robot accelerates, inertia pushes the CoM backward relative
  // to the movement direction. The further the CoM is from the support
  // center, the more torque and thus more tilt.
  //
  // We project the CoM offset onto the robot's local "perpendicular to
  // heading" axis to get the tilt-relevant component.

  const comX = robot.stability.comX; // CoM offset from robot center (local space)
  const comY = robot.stability.comY;

  // Acceleration = velocity change since last frame (in px/s²)
  const pvx = prevVx.get(robot.id) ?? robot.vx;
  const pvy = prevVy.get(robot.id) ?? robot.vy;
  const accelX = (robot.vx - pvx) / dtS;
  const accelY = (robot.vy - pvy) / dtS;
  const accelMag = Math.sqrt(accelX * accelX + accelY * accelY);

  let dynamicTilt = 0;

  if (accelMag > 1) {
    // Robot's perpendicular axis (screen-space: tilt axis is perpendicular to heading)
    // Heading points in movement direction; the "tilt axis" is 90° rotated.
    const hx = Math.cos(robot.heading);
    const hy = Math.sin(robot.heading);
    // Perpendicular to heading (pointing "left" of heading)
    const perpX = -hy;
    const perpY = hx;

    // Project CoM offset onto the perpendicular axis
    // Positive = CoM is to the left of center along movement direction
    const comPerp = comX * perpX + comY * perpY;

    // Project acceleration onto heading (longitudinal)
    const accelLong = accelX * hx + accelY * hy;

    // Torque = CoM offset × acceleration (inertia pulls CoM backward)
    // More CoM offset + more acceleration = more tilt
    // Negative accelLong = braking → CoM swings forward → positive tilt if CoM is above
    dynamicTilt = -comPerp * accelLong * 0.0004;

    // Also add lateral acceleration effect (turning)
    const accelLat = accelX * perpX + accelY * perpY;
    dynamicTilt += accelLat * 0.003;
  }

  // Clamp dynamic tilt
  dynamicTilt = Math.max(-MAX_DYNAMIC_TILT, Math.min(MAX_DYNAMIC_TILT, dynamicTilt));

  // Static instability tilt: proportional to CoM offset from support centroid
  // This is the "resting" tilt when standing still — gravity pulls toward CoM
  const staticTilt = robot.stability.tiltAngle;

  const targetTilt = dynamicTilt + staticTilt;

  // Smooth with different speeds: fast response to acceleration, slow recovery
  const prevTiltVal = visualTilt.get(robot.id) ?? 0;
  const tiltDiff = targetTilt - prevTiltVal;
  const smoothSpeed = Math.abs(tiltDiff) > Math.abs(prevTiltVal) ? TILT_SMOOTHING * 1.5 : TILT_SMOOTHING * 0.5;
  const smoothed = prevTiltVal + tiltDiff * Math.min(1, smoothSpeed * dtS);
  visualTilt.set(robot.id, smoothed);

  // Store velocity for next frame
  prevVx.set(robot.id, robot.vx);
  prevVy.set(robot.id, robot.vy);

  // ── 9. Tipover check (skip if already fallen or flying) ──
  if (fallen || isFlyer) {
    // Skip tipover logic — already toppled, or flyer (flyers spin instead)
  } else {
  // If the total tilt exceeds the critical angle, the robot falls.
  // Very unstable robots (single wheel, columns) have a lower threshold.
  const totalTilt = Math.abs(smoothed);
  const tipProgress = tipoverAccum.get(robot.id) ?? 0;
  // Lower the threshold for very unstable robots: stable=70%, unstable=40% of TIPOVER_ANGLE
  const tipThreshold = TIPOVER_ANGLE * (0.4 + 0.3 * stability);
  if (totalTilt > tipThreshold) {
    // Building toward tipover — instability accelerates the fall
    const buildRate = TIPOVER_BUILDUP * (1 - stability) * dtS;
    const newProgress = tipProgress + buildRate;
    tipoverAccum.set(robot.id, newProgress);
    if (newProgress >= 1.0) {
      // Robot tips over — falls on its side, components take impact damage
      robot.topple(Math.sign(smoothed) || 1);
      return;
    }
  } else {
    // Recovering — slowly reduce tipover accumulator
    tipoverAccum.set(robot.id, Math.max(0, tipProgress - dtS * 0.5));
  }
  } // end if (!fallen)

  // ── 9b. Hop cycle for jumper robots ──
  let hopYOffset = 0;
  let hopScaleX = 1;
  let hopScaleY = 1;
  if (robot.isHopping && !fallen && !isFlyer) {
    // Only cycle when robot has movement intent
    if (hasIntent) {
      robot.jumpTimer += dtS;
      if (robot.jumpTimer >= HOP_CYCLE) robot.jumpTimer -= HOP_CYCLE;
    }
    const t = robot.jumpTimer;

    if (t < HOP_COMPRESS_TIME) {
      // Phase 1: Compression (slow squash) — robot crouches, no horizontal movement
      const p = t / HOP_COMPRESS_TIME; // 0→1
      hopScaleY = 1 - HOP_SQUASH_AMOUNT * p;          // squash down
      hopScaleX = 1 + HOP_SQUASH_AMOUNT * 0.5 * p;    // widen slightly
      // Suppress horizontal velocity during compression
      robot.vx *= 0.3;
      robot.vy *= 0.3;
      robot.jumpPhase = p * 0.5; // 0→0.5
      // Sound: compression buildup near end of phase
      if (p > 0.85 && p - (dtS / HOP_COMPRESS_TIME) <= 0.85) {
        SoundManager.play('jumpCompress');
      }
    } else if (t < HOP_COMPRESS_TIME + HOP_LAUNCH_TIME) {
      // Phase 2: Launch snap (fast release) — spring expands, robot launches upward
      const p = (t - HOP_COMPRESS_TIME) / HOP_LAUNCH_TIME; // 0→1
      hopScaleY = (1 - HOP_SQUASH_AMOUNT) + HOP_SQUASH_AMOUNT * (1 + p * 0.3); // stretch tall
      hopScaleX = (1 + HOP_SQUASH_AMOUNT * 0.5) - HOP_SQUASH_AMOUNT * 0.5 * p;
      hopYOffset = -HOP_HEIGHT * p * 0.3; // start lifting
      robot.jumpPhase = 0.5 + p * 0.1;
      // BOING sound on launch
      if (p < dtS / HOP_LAUNCH_TIME + 0.01) {
        SoundManager.play('jumpBoing');
      }
    } else if (t < HOP_COMPRESS_TIME + HOP_LAUNCH_TIME + HOP_AIR_TIME) {
      // Phase 3: Airborne (parabolic arc) — robot moves freely in the air
      const airT = t - HOP_COMPRESS_TIME - HOP_LAUNCH_TIME;
      const p = airT / HOP_AIR_TIME; // 0→1
      // Parabolic arc: peak at p=0.5
      hopYOffset = -HOP_HEIGHT * 4 * p * (1 - p);
      hopScaleY = 1 + 0.05 * Math.sin(p * Math.PI); // slight stretch at peak
      hopScaleX = 1 - 0.03 * Math.sin(p * Math.PI);
      robot.jumpPhase = 0.6 + p * 0.3;
      // In air: velocity boost (this is when the robot actually covers distance)
      // Velocity is already set by BehaviorEngine intent — just let it flow
    } else {
      // Phase 4: Landing squash (fast)
      const p = (t - HOP_COMPRESS_TIME - HOP_LAUNCH_TIME - HOP_AIR_TIME) / HOP_LAND_TIME;
      hopScaleY = 1 - HOP_SQUASH_AMOUNT * 0.6 * (1 - p);  // squash on land, recover
      hopScaleX = 1 + HOP_SQUASH_AMOUNT * 0.3 * (1 - p);
      // Dampen velocity on landing
      robot.vx *= 0.5;
      robot.vy *= 0.5;
      robot.jumpPhase = 0.9 + p * 0.1;
    }
  }

  // ── 10. Apply to container ──
  // When fallen, topple system handles container rotation/position — skip visual updates
  if (!fallen) {
    if (isFlyer) {
      // Flying robots: pure rotation, no flip.
      robot.flipped = false;
      robot.container.setScale(robot.renderScale, robot.renderScale);
      robot.container.setRotation(robot.heading - robot.buildHeading + smoothed);
    } else {
      // Ground robots: horizontal flip + tilt + hop squash/stretch
      const sx = robot.flipped ? -robot.renderScale : robot.renderScale;
      robot.container.setScale(sx * hopScaleX, robot.renderScale * hopScaleY);
      const appliedTilt = robot.flipped ? -smoothed : smoothed;
      robot.container.setRotation(appliedTilt);
    }
    robot.container.setPosition(robot.x, robot.y + hopYOffset);
  }

  // Clear intent for next tick (behavior must set it each frame)
  robot.moveIntentX = 0;
  robot.moveIntentY = 0;
  robot.moveIntentSpeed = 0;
}

/**
 * Resolve robot-robot collisions: push overlapping robots apart.
 * Call once per frame after all individual physics updates.
 */
// Reusable array to avoid allocation every frame
const _aliveBuffer: Robot[] = [];

/** Clamp a position to map bounds and prevent ground units from entering walls */
function clampToValid(x: number, y: number, isFlyer: boolean): { x: number; y: number } {
  const margin = TILE_SIZE / 2;
  let cx = Math.max(margin, Math.min(GAME_WIDTH - margin, x));
  let cy = Math.max(margin, Math.min(GAME_HEIGHT - margin, y));
  if (isFlyer) return { x: cx, y: cy };

  const col = Math.floor(cx / TILE_SIZE);
  const row = Math.floor(cy / TILE_SIZE);
  const tile = MAP_GRID[row]?.[col];
  const walkable = tile === TileType.PATH || tile === TileType.PLACEMENT ||
    tile === TileType.BROKEN_PLACEMENT || tile === TileType.BASE ||
    tile === TileType.SPAWN || tile === TileType.MUD || tile === TileType.GRILLE ||
    tile === TileType.GROUND;
  if (!walkable) {
    // Reject push — keep original position
    cx = x; cy = y;
  }
  return { x: cx, y: cy };
}

export function resolveRobotCollisions(robots: Robot[]): void {
  _aliveBuffer.length = 0;
  for (let k = 0; k < robots.length; k++) {
    if (robots[k].alive) _aliveBuffer.push(robots[k]);
  }
  const alive = _aliveBuffer;
  for (let i = 0; i < alive.length; i++) {
    for (let j = i + 1; j < alive.length; j++) {
      const a = alive[i];
      const b = alive[j];
      // Flying robots pass over ground robots — no collision
      const aFlyer = a.hasWings();
      const bFlyer = b.hasWings();
      if (aFlyer !== bFlyer) continue;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const distSq = dx * dx + dy * dy;
      const minDist = a.physicsRadius + b.physicsRadius;
      if (distSq < minDist * minDist && distSq > 0.0001) {
        const dist = Math.sqrt(distSq);
        const overlap = minDist - dist;
        const nx = dx / dist;
        const ny = dy / dist;
        const push = overlap * 0.5;
        // Push apart — clamp to valid tiles
        const aNew = clampToValid(a.x - nx * push, a.y - ny * push, aFlyer);
        const bNew = clampToValid(b.x + nx * push, b.y + ny * push, bFlyer);
        a.x = aNew.x; a.y = aNew.y;
        b.x = bNew.x; b.y = bNew.y;
        // Bounce velocities
        const relVn = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny;
        if (relVn < 0) {
          a.vx += relVn * nx * 0.5;
          a.vy += relVn * ny * 0.5;
          b.vx -= relVn * nx * 0.5;
          b.vy -= relVn * ny * 0.5;
          // Clank sound on significant collision (speed-gated + probabilistic)
          if (Math.abs(relVn) > 30 && Math.random() < 0.3) {
            SoundManager.play('robotClank');
          }
        }
        // Update container positions
        a.container.setPosition(a.x, a.y);
        b.container.setPosition(b.x, b.y);
      }
    }
  }
}
